'use strict';

import getVideoId from 'get-video-id';
import {
    youtube as youtubeOembed
} from '../utils/oembed';

const SELECTOR_CLASS = 'js-video-embed';
const THUMBNAIL_SELECTOR = '[data-youtube-url]';

function populateThumbnails() {
    const images = document.querySelectorAll(THUMBNAIL_SELECTOR);

    for (const image of images) {
        const { id, service } = getVideoId(image.getAttribute('data-youtube-url'));

        if (service !== 'youtube' || !id) {
            continue;
        }

        image.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
}

let youtubeApiPromise;

function loadYouTubeAPI() {
    if (youtubeApiPromise) {
        return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(resolve => { // eslint-disable-line compat/compat
        if (window.YT?.Player) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';

        const previous = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            resolve();
        }

        document.head.appendChild(script);
    });

    return youtubeApiPromise;
}

function createYouTubePlayer(url, id, oembed) {
    let player;

    return {
        async mount(container) {
            await loadYouTubeAPI();

            const dummy = document.createElement('div');
            dummy.id = `${container.id}-player`;
            container.appendChild(dummy);

            return new Promise(resolve => { // eslint-disable-line
                player = new YT.Player(dummy.id, { // eslint-disable-line no-undef
                    videoId: id,
                    playerVars: {
                        autoplay: 0,
                        playlist: id,
                    },
                    events: {
                        onReady: resolve,
                    },
                });
            });
        },

        play() {
            player && player.playVideo();
        },

        pause() {
            player && player.pauseVideo();
        },

        stop() {
            player && player.pauseVideo();
            player && player.seekTo(0);
        },

        reset() {
            player && player.seekTo(0);
        },
    }
}

const createEmbed = (element, url, id) => {
    const embedId = `video-embed-${id}`;

    const media = document.createElement('div');
    media.id = `${embedId}-media`;
    media.className = 'video-embed__media video-embed__media--loading';

    const loader = document.createElement('div');
    loader.className = 'video-embed__loader';
    loader.setAttribute('aria-label', 'Loading video');
    loader.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`;

    media.appendChild(loader);

    element.replaceWith(media);

    const player = createYouTubePlayer(url, id);

    player.mount(media).then(async () => {
        loader.remove();

        const oembed = await youtubeOembed(url);

        const aspectRatio = oembed.width / oembed.height;
        media.style.setProperty('--aspect-ratio', aspectRatio);

        if (aspectRatio > 1) {
            media.classList.add('video-embed__media--landscape');
        } else if (aspectRatio < 1) {
            media.classList.add('video-embed__media--portrait');
        } else {
            media.classList.add('video-embed__media--square');
        }

        media.classList.remove('video-embed__media--loading');
        media.classList.add('video-embed__media--loaded');

        player.play();
    });
}

export async function init() {
    populateThumbnails();

    const elements = document.querySelectorAll(`.${SELECTOR_CLASS}`);

    for (const element of elements) {
        const url = element.href;

        const { id, service } = getVideoId(url);

        if (service !== 'youtube' || !id) {
            continue;
        }

        element.addEventListener('click', event => {
            event.preventDefault();

            createEmbed(element, url, id);
        }, { once: true });

        element.classList.add(`${SELECTOR_CLASS}--initialised`);
    }
}
